import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import { getRecommendationChart } from 'Shared/actions/charts';
import LineChart from 'Shared/components/charts/LineChart';

const intervalForTimeRange = timeRangeCode => {
  if (timeRangeCode === 'last_90days') {
    return 'W';
  }
  if (timeRangeCode === 'last_360days') {
    return 'M';
  }
  return 'D';
};

const TimeSeriesChart = ({ chartKey, setDetailNudgesFilter, title, userId = undefined, tags = [] }) => {
  const dispatch = useDispatch();

  const chartData = useSelector(state => state.charts[chartKey]);

  const [timeRange, setTimeRange] = useState({ value: 'last_30days', label: 'Last 30 Days' });

  const loadData = range => {
    dispatch(
      getRecommendationChart({
        chartCode: chartKey,
        timeFilters: { time_interval: intervalForTimeRange(range), time_range_code: range },
        userId,
        tags: tags?.map(({ value }) => value),
      })
    );
  };

  useEffect(() => {
    loadData(timeRange.value);
  }, [timeRange, tags]);

  const options_with_onClick = ({
    chart: {
      options,
      series,
      x_axis_values,
      time_filters: { time_interval },
    },
  }) => {
    if (series.length === 0 || !series[0].recommendations_request) return options;

    return {
      ...options,
      onClick: (e, item) => {
        e.stopPropagation();
        if (item.length === 0) return;

        const chart_element = item[0]._chart.getElementsAtEventForMode(e, 'point')[0];
        const series_index = chart_element._datasetIndex;
        const x_axis_index = chart_element._index;

        const requested_series = series[series_index];
        const start_time = x_axis_values[x_axis_index];
        const end_time = start_time;
        setDetailNudgesFilter({
          start_time,
          end_time,
          time_interval,
          user_id: requested_series.recommendations_request.user_id,
          recommendation_event: requested_series.recommendations_request.event,
          tags: tags?.map(({ value }) => value),
        });
      },
    };
  };

  return (
    <>
      <h5 className="mt-0 mb-4">{title}</h5>
      <div className="timerange-selector d-flex justify-content-end align-items-center">
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          defaultValue={timeRange}
          options={[
            { value: 'last_30days', label: 'Last 30 Days' },
            { value: 'last_60days', label: 'Last 60 Days' },
            { value: 'last_90days', label: 'Last 90 Days' },
            { value: 'last_360days', label: 'Last 360 Days' },
            { value: 'prev_month', label: 'Previous Month' },
          ]}
          isSearchable
          onChange={option => setTimeRange(option)}
        />
      </div>
      <div className="nudgebot-chart">
        {(!chartData || chartData.loading) && <div>loading...</div>}
        {chartData && !chartData.loading && (
          <LineChart
            options={options_with_onClick(chartData)}
            datasets={chartData.chart.datasets}
            labels={chartData.chart.labels}
          />
        )}
      </div>
    </>
  );
};

TimeSeriesChart.propTypes = {
  chartKey: PropTypes.string.isRequired,
  setDetailNudgesFilter: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  userId: PropTypes.string,
  tags: PropTypes.instanceOf(Array),
};

export default TimeSeriesChart;
